import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { 
  HelpCircle, 
  Home, 
  Key, 
  ClipboardList, 
  BarChart3, 
  Settings, 
  AlertTriangle, 
  Rocket,
  CheckCircle2
} from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

interface FAQItem {
  question: string;
  answer: string | string[];
}

interface FAQCategory {
  id: string;
  label: string;
  shortLabel: string;
  icon: React.ElementType;
  items: FAQItem[];
}

const faqCategories: FAQCategory[] = [
  {
    id: "utilite",
    label: "À quoi sert Immovox ?",
    shortLabel: "Utilité",
    icon: HelpCircle,
    items: [
      {
        question: "Concrètement, qu'est-ce qu'Immovox m'apporte ?",
        answer: [
          "Immovox vous permet de savoir exactement ce que veut chaque appelant avant même de le rappeler.",
          "Aujourd'hui, quand vous recevez un appel manqué, vous rappelez sans savoir s'il s'agit d'un acheteur sérieux, d'un curieux qui « regarde les prix », ou d'un candidat locataire dont le dossier ne tiendra jamais. Vous perdez du temps à poser des questions de base, à comprendre le contexte, à trier.",
          "Avec Immovox, ce travail est fait en amont. Chaque appel génère une fiche structurée : intention (achat, vente, location, renseignement), informations clés collectées, niveau de priorité. Quand vous ouvrez votre dashboard, vous savez immédiatement quoi traiter en premier."
        ]
      },
      {
        question: "En quoi c'est différent d'un simple répondeur ou d'une permanence téléphonique ?",
        answer: [
          "Un répondeur enregistre un message. Une permanence prend un nom et un numéro. Immovox qualifie.",
          "L'IA pose les questions que vous poseriez vous-même : quel type de projet ? Quel secteur ? Quel budget ? Quelle urgence ? Pour le locatif : quelle situation professionnelle ? Quels revenus ? Des garants ?",
          "Vous ne recevez pas juste « M. Dupont a appelé ». Vous recevez « M. Dupont cherche un T3 sur le centre-ville, budget 250-280k€, financement validé, souhaite visiter cette semaine ».",
          "La différence, c'est que vous pouvez agir immédiatement, avec toutes les cartes en main."
        ]
      },
      {
        question: "Immovox fonctionne-t-il pour les agences en Belgique francophone ?",
        answer: [
          "Oui, Immovox est pleinement compatible avec les agences immobilières situées à Bruxelles et en Wallonie.",
          "Vous pouvez obtenir un numéro local belge (+32) directement depuis le back-office, sans configuration supplémentaire. L'IA francophone traite vos appels avec la même qualité de qualification qu'en France, et le dashboard reste identique.",
          "Le renvoi d'appel depuis un numéro belge existant est également supporté."
        ]
      }
    ]
  },
  {
    id: "achat-vente",
    label: "Opportunités achat / vente",
    shortLabel: "Achat/Vente",
    icon: Home,
    items: [
      {
        question: "Comment Immovox distingue un vrai projet d'une simple curiosité ?",
        answer: [
          "C'est l'un des plus gros gains de temps pour les agents : ne plus rappeler des gens qui « regardent juste les prix » ou qui n'ont pas de projet défini.",
          "Immovox identifie l'intention dès les premières secondes de l'échange. L'appelant cherche-t-il à acheter ? À vendre ? Veut-il une estimation ? Pose-t-il simplement une question sur une annonce ?",
          "Pour les projets concrets, l'IA creuse : type de bien, secteur, budget, délai, situation du financement. Pour les demandes floues, elle collecte le minimum et vous restitue un contexte clair.",
          "Résultat : vous ne passez plus 20 minutes au téléphone avec quelqu'un qui n'a pas de projet. Vous le voyez dans la fiche, et vous décidez comment (et si) vous le recontactez."
        ]
      },
      {
        question: "Et pour les vendeurs potentiels ?",
        answer: [
          "Les appels de vendeurs sont souvent les plus précieux — et les plus faciles à rater quand on est occupé.",
          "Quand quelqu'un appelle pour vendre ou demander une estimation, Immovox le détecte et collecte les informations essentielles : adresse ou secteur du bien, type de bien, raison de la vente, délai envisagé.",
          "Vous êtes alerté immédiatement avec une fiche complète. Vous pouvez rappeler dans l'heure avec un contexte précis, ce qui fait toute la différence face à un concurrent qui rappelle « à l'aveugle »."
        ]
      },
      {
        question: "Est-ce qu'Immovox remplace mon travail de qualification commerciale ?",
        answer: [
          "Non, et c'est important de le comprendre.",
          "Immovox ne prend aucune décision commerciale. Il ne juge pas si un projet est « bon » ou « mauvais ». Il structure les informations pour que vous puissiez, vous, prendre la bonne décision.",
          "C'est un outil de préparation, pas de remplacement. Votre expertise reste au cœur de la relation client — Immovox vous permet juste d'arriver mieux préparé."
        ]
      }
    ]
  },
  {
    id: "locatif",
    label: "Appels locatifs",
    shortLabel: "Locatif",
    icon: Key,
    items: [
      {
        question: "Le locatif, c'est vraiment là qu'Immovox fait la différence ?",
        answer: [
          "Oui, c'est souvent le cas d'usage le plus impactant.",
          "Une annonce locative peut générer 30, 50, parfois 100 appels. La majorité des appelants ne correspondent pas au profil recherché : revenus insuffisants, pas de garants, situation instable, délai incompatible.",
          "Aujourd'hui, vous passez des heures à rappeler ces personnes une par une, à leur poser les mêmes questions, à constater que le dossier ne tient pas.",
          "Avec Immovox, ces informations sont collectées dès le premier appel. Quand vous ouvrez votre dashboard, vous voyez immédiatement qui a le bon profil. Vous ne rappelez que les candidats pertinents."
        ]
      },
      {
        question: "Quelles informations Immovox collecte pour le locatif ?",
        answer: [
          "Vous définissez vous-même ce qui est important. Les informations courantes incluent :",
          "• Situation professionnelle : CDI, CDD, indépendant, étudiant, retraité",
          "• Niveau de revenus (ou multiple du loyer)",
          "• Présence de garants (physiques ou via garantie type Visale)",
          "• Composition du foyer",
          "• Date d'entrée souhaitée",
          "• Budget maximum",
          "Ces éléments vous permettent de filtrer instantanément. Un locataire avec des revenus à 2x le loyer et sans garant ? Vous le voyez avant de décrocher."
        ]
      },
      {
        question: "Comment ça se passe pour le candidat qui appelle ?",
        answer: [
          "L'expérience est fluide et professionnelle. Le candidat appelle, Immovox répond au nom de votre agence, pose quelques questions naturelles sur son projet.",
          "Ce n'est pas un interrogatoire — c'est une conversation structurée. Le candidat a l'impression de parler à quelqu'un qui s'intéresse à sa situation.",
          "À la fin de l'appel, il sait que sa demande a été prise en compte. De votre côté, vous avez une fiche complète prête à être traitée."
        ]
      }
    ]
  },
  {
    id: "apres-appel",
    label: "Après l'appel",
    shortLabel: "Après l'appel",
    icon: ClipboardList,
    items: [
      {
        question: "Qu'est-ce que je reçois concrètement après chaque appel ?",
        answer: [
          "Après chaque appel, un ticket est créé automatiquement dans votre dashboard. Il contient :",
          "• L'intention identifiée (achat, vente, location, estimation, renseignement)",
          "• Les informations collectées pendant l'échange",
          "• Une synthèse en quelques lignes",
          "• Un niveau de priorité (calculé selon vos critères)",
          "• Les coordonnées de l'appelant",
          "Vous recevez également un email instantané pour chaque appel qualifié. Votre équipe peut s'attribuer le ticket et agir.",
          "Tout est centralisé : vous ouvrez le dashboard, vous voyez ce qu'il y a à traiter, vous agissez."
        ]
      },
      {
        question: "Puis-je réécouter ou relire un appel ?",
        answer: [
          "Oui. Chaque appel est transcrit et consultable. L'enregistrement audio est également disponible.",
          "C'est utile pour vérifier une information que vous n'êtes pas sûr d'avoir bien comprise, éviter les malentendus avant un rappel, transmettre un dossier à un collaborateur avec le contexte complet, ou garder une trace en cas de litige."
        ]
      },
      {
        question: "Comment fonctionne le système de priorité ?",
        answer: [
          "Vous définissez vous-même ce qui constitue un appel prioritaire.",
          "Par exemple : tout projet d'achat avec financement validé = priorité haute. Demande de location avec revenus > 3x le loyer = priorité haute. Simple demande d'information = priorité basse.",
          "Immovox applique vos règles automatiquement. Quand vous ouvrez le dashboard, les fiches les plus importantes remontent en premier. Vous ne passez plus à côté d'une opportunité parce qu'elle était noyée dans la masse."
        ]
      }
    ]
  },
  {
    id: "dashboard",
    label: "Vision globale",
    shortLabel: "Dashboard",
    icon: BarChart3,
    items: [
      {
        question: "À quoi ressemble le dashboard au quotidien ?",
        answer: [
          "Le dashboard est conçu pour l'action, pas pour l'analyse passive.",
          "En un coup d'œil, vous voyez : combien d'appels sont à traiter, lesquels sont prioritaires, qui concerne le locatif vs l'achat/vente, quels appels datent d'aujourd'hui vs hier.",
          "Vous pouvez filtrer par type de projet, par priorité, par statut (nouveau, en cours, traité). L'objectif : savoir en 10 secondes où mettre votre énergie."
        ]
      },
      {
        question: "Est-ce que je peux suivre l'activité de toute mon équipe ?",
        answer: [
          "Oui. Le dashboard vous donne une vision globale de l'activité téléphonique de l'agence.",
          "Vous voyez qui a traité quoi, quels appels sont encore en attente, comment se répartit le volume entre locatif et transaction.",
          "C'est utile pour équilibrer la charge de travail, identifier les pics d'activité, et s'assurer qu'aucune demande ne tombe dans l'oubli."
        ]
      },
      {
        question: "Quelles statistiques sont disponibles ?",
        answer: [
          "Au-delà du suivi opérationnel, Immovox vous donne des insights sur votre activité :",
          "• Volume d'appels par jour, semaine, mois",
          "• Répartition par type de projet (achat, vente, location, autre)",
          "• Taux de fiches complètes vs incomplètes",
          "• Temps de réponse moyen (délai entre l'appel et le rappel)",
          "• Sources des appels (si vous utilisez plusieurs numéros)",
          "Ces données vous aident à comprendre votre activité réelle — pas juste votre ressenti."
        ]
      }
    ]
  },
  {
    id: "parametrage",
    label: "Paramétrage",
    shortLabel: "Paramétrage",
    icon: Settings,
    items: [
      {
        question: "Est-ce que le discours est le même pour toutes les agences ?",
        answer: [
          "Non. Chaque agence a son identité, son ton, ses priorités.",
          "Vous pouvez personnaliser : le message d'accueil, la façon dont Immovox se présente, les questions posées selon le type de projet, les critères de priorité, le vocabulaire utilisé.",
          "L'objectif est que l'appelant ait l'impression de parler à votre agence, pas à un robot générique. Votre identité est préservée."
        ]
      },
      {
        question: "Puis-je ajuster les critères de qualification pour le locatif ?",
        answer: [
          "Oui, c'est même recommandé.",
          "Chaque agence a ses propres critères : certaines exigent 3x le loyer en revenus, d'autres acceptent 2.5x avec garant. Certaines refusent les CDD, d'autres les acceptent sous conditions.",
          "Vous définissez vos règles, Immovox les applique. Les fiches sont tagguées automatiquement : « correspond aux critères », « à vérifier », « hors critères ».",
          "Vous gardez le contrôle total sur ce qui constitue un bon dossier pour vous."
        ]
      },
      {
        question: "Est-ce que je peux créer des réponses automatiques aux questions fréquentes ?",
        answer: [
          "Oui. C'est particulièrement utile pour le locatif où les mêmes questions reviennent constamment :",
          "• « Quels documents faut-il pour le dossier ? »",
          "• « Est-ce que vous acceptez les garants ? »",
          "• « C'est quoi les charges ? »",
          "• « Est-ce que les animaux sont acceptés ? »",
          "Vous intégrez vos réponses types, Immovox les utilise quand c'est pertinent. Votre équipe n'a plus à répéter les mêmes explications 50 fois par jour."
        ]
      }
    ]
  },
  {
    id: "problemes",
    label: "Et si ça se passe mal ?",
    shortLabel: "Problèmes",
    icon: AlertTriangle,
    items: [
      {
        question: "Que se passe-t-il si l'appelant ne veut pas parler à une IA ?",
        answer: [
          "C'est rare, mais ça arrive.",
          "Dans ce cas, Immovox ne force rien. Il peut proposer un rappel par un conseiller, ou simplement collecter le nom et le numéro pour que vous rappeliez.",
          "L'expérience reste professionnelle. L'appelant n'est jamais bloqué ou frustré."
        ]
      },
      {
        question: "Que se passe-t-il si un appel est confus ou mal compris ?",
        answer: [
          "Immovox ne prend pas d'initiative risquée. Si l'échange est confus, il collecte ce qu'il peut et vous restitue le contexte honnêtement.",
          "La fiche indiquera clairement que l'intention n'a pas pu être identifiée avec certitude. Vous pouvez alors réécouter l'appel ou rappeler pour clarifier.",
          "L'IA ne « devine » pas — elle admet ses limites."
        ]
      },
      {
        question: "Et pour les appels tendus ou conflictuels ?",
        answer: [
          "Immovox reste factuel et neutre. Il ne s'engage pas dans des discussions conflictuelles, ne promet rien, ne prend pas parti.",
          "Si l'appelant est agressif ou déraisonnable, l'échange reste courtois et se termine proprement. Vous récupérez une fiche avec le contexte, et vous décidez comment gérer.",
          "Votre image reste professionnelle, même dans les situations difficiles."
        ]
      }
    ]
  },
  {
    id: "mise-en-place",
    label: "Mise en place",
    shortLabel: "Démarrage",
    icon: Rocket,
    items: [
      {
        question: "Combien de temps pour être opérationnel ?",
        answer: [
          "En général, 48 heures.",
          "Le processus : inscription en ligne, configuration de votre assistant, test sur quelques appels pour valider que tout fonctionne, puis mise en production.",
          "Vous pouvez tout faire en autonomie, ou planifier un rendez-vous avec notre équipe pour un accompagnement personnalisé.",
          "Le transfert d'appels se configure depuis votre ligne existante — aucune compétence technique requise."
        ]
      },
      {
        question: "Est-ce que je dois changer de numéro de téléphone ?",
        answer: [
          "Non. Vous gardez votre numéro habituel.",
          "Immovox vous attribue un numéro français local (non surtaxé) qui reste invisible pour vos clients. Ils continuent d'appeler votre numéro habituel.",
          "Le transfert vers Immovox se configure sur votre ligne existante : en renvoi d'appel (après X sonneries) ou en transfert direct selon votre stratégie. Quand vous êtes disponible, vous décrochez normalement. Quand vous êtes occupé ou indisponible, les appels sont redirigés vers Immovox."
        ]
      },
      {
        question: "Quelle est la différence entre renvoi d'appel et transfert d'appel ?",
        answer: [
          "Ce sont deux façons d'activer Immovox, selon votre stratégie :",
          "• Renvoi d'appel (débordement) : Votre téléphone sonne d'abord. Si vous ne décrochez pas après X sonneries, l'appel est automatiquement redirigé vers Immovox. Idéal pour prendre les appels quand vous êtes disponible, et laisser Immovox gérer quand vous êtes occupé.",
          "• Transfert d'appel (direct) : Tous les appels vont directement vers Immovox, sans sonner chez vous. Idéal pour les horaires de fermeture ou si vous souhaitez qu'Immovox traite 100% des appels.",
          "Vous pouvez basculer d'un mode à l'autre à tout moment depuis les paramètres de votre ligne téléphonique. La configuration se fait en quelques clics, sans compétence technique."
        ]
      },
      {
        question: "Puis-je tester avant de m'engager ?",
        answer: [
          "Oui. Vous bénéficiez de 7 jours d'essai gratuit, sans engagement.",
          "Pendant cette période, vous testez Immovox en conditions réelles sur vos propres appels.",
          "Si c'est pertinent pour vous, vous continuez. Sinon, vous arrêtez sans frais."
        ]
      },
      {
        question: "Puis-je arrêter ou ajuster à tout moment ?",
        answer: [
          "Oui. Vous n'êtes jamais enfermé dans un fonctionnement figé.",
          "Vous pouvez activer ou désactiver Immovox quand vous voulez (par exemple, le désactiver quand vous êtes disponible au bureau).",
          "Vous pouvez ajuster les paramètres, les questions, les critères de priorité au fil du temps.",
          "L'outil s'adapte à vous, pas l'inverse."
        ]
      }
    ]
  }
];

const summaryPoints = [
  "Savoir ce que veut chaque appelant avant de rappeler",
  "Identifier les projets sérieux vs les simples curiosités",
  "Trier les dossiers locatifs en quelques secondes",
  "Reprendre le contrôle de votre temps"
];

const FAQSection = () => {
  const containerRef = useScrollReveal({ staggerDelay: 200 });

  const faqPageLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqCategories.flatMap((category) =>
      category.items.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: Array.isArray(item.answer) ? item.answer.join("\n\n") : item.answer,
        },
      })),
    ),
  };

  return (
    <section id="faq" className="py-16 bg-secondary/55 md:py-20 lg:py-24">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqPageLd) }}
      />
      <div className="mx-auto max-w-[1120px] px-5 md:px-8 relative z-10" ref={containerRef}>
        {/* Header */}
        <div className="mb-10 max-w-3xl scroll-reveal-item" data-scroll-item>
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-medium text-sm mb-6">
            FAQ
          </span>
          <h2 className="text-4xl md:text-[48px] font-semibold tracking-[-.04em] text-foreground mb-4">
            Questions fréquentes
          </h2>
          <p className="text-[16px] leading-7 text-muted-foreground max-w-2xl">
            Tout ce que vous devez savoir sur la qualification d'appels avec Immovox
          </p>
        </div>

        {/* Tabs + Accordion */}
        <div className="scroll-reveal-item" data-scroll-item>
        <Tabs defaultValue="utilite" className="w-full">
          <TabsList className="w-full flex-wrap h-auto gap-1 bg-transparent border-y border-border p-0 py-3 mb-8 rounded-none justify-start">
            {faqCategories.map((category) => {
              const Icon = category.icon;
              return (
                <TabsTrigger
                  key={category.id}
                  value={category.id}
                  className="flex items-center gap-2 px-3 py-2 text-sm rounded-none transition-colors data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{category.shortLabel}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {faqCategories.map((category) => (
            <TabsContent key={category.id} value={category.id} className="mt-0">
              <div className="border-t border-foreground/20">
                <h3 className="text-xl font-semibold text-foreground py-6 flex items-center gap-3">
                  <div className="w-9 h-9 border border-border flex items-center justify-center">
                    <category.icon className="h-5 w-5 text-primary" />
                  </div>
                  {category.label}
                </h3>
                
                <Accordion type="single" collapsible className="w-full">
                  {category.items.map((item, index) => (
                    <AccordionItem 
                      key={index} 
                      value={`item-${index}`}
                      className="border-border"
                    >
                      <AccordionTrigger className="text-left hover:no-underline hover:text-primary py-5 text-[15px] font-medium">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-[14px] leading-7 text-muted-foreground pb-5">
                        {Array.isArray(item.answer) ? (
                          <div className="space-y-3">
                            {item.answer.map((paragraph, pIndex) => (
                              <p key={pIndex}>{paragraph}</p>
                            ))}
                          </div>
                        ) : (
                          <p>{item.answer}</p>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </TabsContent>
          ))}
        </Tabs>
        </div>

        {/* Summary Block */}
        <div className="mt-10 border-t border-primary/30 pt-7 scroll-reveal-item" data-scroll-item>
          <h3 className="text-xl font-semibold text-foreground mb-2">
            En résumé
          </h3>
          <p className="text-muted-foreground mb-6">
            Immovox n'est pas un standard téléphonique. C'est un <strong className="text-foreground">outil de qualification qui vous fait gagner du temps</strong>.
          </p>
          <p className="text-muted-foreground mb-4">Il vous permet de :</p>
          <ul className="space-y-3">
            {summaryPoints.map((point, index) => (
              <li key={index} className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <span className="text-foreground">{point}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
